/**
 * Real-vs-Mock Matrix — the canonical clinician/data-team contract. Binds GET
 * /api/v1/data-health/matrix and renders each tile's AUTHORITATIVE MatrixTile.status string DIRECTLY
 * (REAL / PARTIAL / MOCK / DEFERRED) mapped to a SkyBadge by color + label. The server already computed
 * the status from the static tile->model registry against the live manifest, so it can never drift from
 * what the models emit. deriveBadge is intentionally NOT used here (Pitfall 3): re-deriving could
 * disagree with the server. Live truth is 9 REAL / 7 DEFERRED.
 */
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  SkyBadge,
  SkyDataGrid,
  SkyDropdown,
  SkyLink,
  SkyMessageBar,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type { MatrixTile } from "@shared/contracts";
import { usePage } from "../lib/usePage";
import { matrixTilesSchema } from "../lib/schemas/dataHealth";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

/** Authoritative status -> SkyBadge color (status by color + visible label, never color alone).
 *  Exported for unit testing: this map renders MatrixTile.status DIRECTLY (it must never route through
 *  deriveBadge — that is for Provenance objects, and re-deriving here could disagree with the server). */
export const STATUS_COLOR: Record<MatrixTile["status"], "success" | "warning" | "subtle"> = {
  REAL: "success",
  PARTIAL: "warning",
  MOCK: "warning",
  DEFERRED: "subtle",
};

/** Each tile's originating screen (drill target). A tile with no dedicated screen maps to null (no link),
 *  never to a fabricated destination. Member-level surfaces deep-link to the conditions-richest member. */
export const TILE_ROUTE: Record<string, string | null> = {
  roster: "/roster",
  "problem-list": "/member",
  "care-gaps": "/care-gaps",
  "quality-measures": "/quality",
  "quality-rates": "/quality",
  "measure-catalog": "/quality",
  vitals: "/vitals",
  encounters: "/encounters",
  providers: "/idt",
  "care-plan": "/idt",
  goals: "/idt",
  medications: "/medications",
  utilization: "/utilization",
  hcc: null,
  census: "/census",
  "compliance-timers": "/idt",
  allergies: "/member",
  immunizations: "/member",
  procedures: "/member",
  coverage: "/member",
  // Newly-registered gold surfaces (A8). measure-scores back the org care-gap worklist; the rest are
  // member/encounter-level evidence. screening-catalog has no dedicated screen yet → null (no fake link).
  documentation: "/member",
  "diagnostic-reports": "/member",
  "measure-scores": "/care-gaps",
  "screening-catalog": null,
  "encounter-diagnoses": "/encounters",
};

function StatusBadge({ status }: { status: MatrixTile["status"] }) {
  // Render-shape guard: an unrecognized/drifted status string falls back to the neutral "subtle" color
  // (never an undefined color prop that could throw), so the badge degrades instead of crashing the grid.
  return (
    <SkyBadge color={STATUS_COLOR[status] ?? "subtle"} appearance="tint" size="small" aria-label={status}>
      {status}
    </SkyBadge>
  );
}

/** Status filter values: the user can narrow to a single provenance status, or see all. */
const STATUS_OPTIONS = [
  { value: "ALL", text: "All statuses" },
  { value: "REAL", text: "REAL" },
  { value: "PARTIAL", text: "PARTIAL" },
  { value: "MOCK", text: "MOCK" },
  { value: "DEFERRED", text: "DEFERRED" },
];

export function Matrix() {
  const navigate = useNavigate();
  const { data, loading, error, retry } = usePage<MatrixTile[]>(
    "/api/v1/data-health/matrix",
    matrixTilesSchema,
  );
  const [statusFilter, setStatusFilter] = useState("ALL");

  const all = useMemo(() => data ?? [], [data]);
  const filtered = useMemo(
    () => (statusFilter === "ALL" ? all : all.filter((t) => t.status === statusFilter)),
    [all, statusFilter],
  );

  const real = all.filter((t) => t.status === "REAL").length;
  const deferred = all.filter((t) => t.status === "DEFERRED").length;

  const columns: SkyDataGridColumn<MatrixTile>[] = [
    {
      key: "tile",
      header: "Tile",
      width: "28%",
      // Drill to the tile's originating screen. A tile with no dedicated screen renders as plain text.
      renderCell: (_v, row) => {
        const to = TILE_ROUTE[row.tile] ?? null;
        return to ? (
          <SkyLink
            href={to}
            onClick={(e) => {
              e.preventDefault();
              navigate(to);
            }}
          >
            {row.tile}
          </SkyLink>
        ) : (
          row.tile
        );
      },
    },
    { key: "backing_model", header: "Backing model", width: "30%" },
    {
      key: "unblocking_feed",
      header: "Unblocking feed",
      width: "22%",
      renderCell: (_v, row) => row.unblocking_feed ?? "Live",
    },
    {
      key: "status",
      header: "Status",
      width: "20%",
      // Render the server-authoritative status string directly. No deriveBadge here.
      renderCell: (_v, row) => <StatusBadge status={row.status} />,
    },
  ];

  return (
    <>
      <PageHeader
        crumbs={[
          { key: "home", label: "skyCare" },
          { key: "data-health", label: "Data Health & Matrix", to: "/data-health" },
          { key: "matrix", label: "Matrix" },
        ]}
        title="Real-vs-Mock Matrix"
        description="The canonical clinician-to-data-team contract: every tile by authoritative status, backing model, and the feed that unblocks it. Filter by status and open any tile's screen. Rendered directly from the server, never re-derived."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        {data && !loading && (
          <SkyMessageBar
            intent="info"
            title="Provenance summary"
            message={`${real} tile(s) REAL, ${deferred} DEFERRED across ${all.length} tracked tiles (incl. the live gold/silver surfaces).`}
          />
        )}
        {/* Loading rule: grid-led screens let SkyDataGrid's `loading` prop own the skeleton — no standalone
            SkyLoader stacked above it. Keep the standalone SkyLoader only on non-grid screens. */}
        {error && <PageError title="Could not load matrix" message={error} onRetry={retry} />}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div className="w-56 max-w-full">
            <SkyDropdown
              label="Filter by status"
              options={STATUS_OPTIONS}
              selectedOptions={[statusFilter]}
              onOptionSelect={(v) => setStatusFilter(v[0] ?? "ALL")}
            />
          </div>
          <SkyLink
            href="/data-health"
            onClick={(e) => {
              e.preventDefault();
              navigate("/data-health");
            }}
          >
            Back to the data-health manifest
          </SkyLink>
        </div>
        <SkyDataGrid<MatrixTile>
          data={filtered}
          columns={columns}
          getRowId={(r, i) => r.tile ?? String(i)}
          loading={loading}
          emptyMessage={statusFilter === "ALL" ? "Awaiting feed" : `No tiles with status ${statusFilter}`}
        />
      </section>
      <PageFooter />
    </>
  );
}
